import { ConfiguratorProps } from 'views/ProductPage/configurators/index';
import { rm, NO_ELEMENT } from 'utils';
import { useReducer, Reducer, useEffect, useMemo, ChangeEventHandler } from 'react';
import connect from 'components/Shared/connect';
import { isValidZipcode } from 'views/CheckoutPage/validation';
import {
  PolarPumpAddonProduct,
  PolarpumpInstallationConfigurator,
  InstallationProducType,
  InstallationAddonGroup
} from 'models/InstallationConfigurator';
import { isZipCodeInRange } from 'views/ProductPage/configurators/installation/isZipCodeInRange';
import {
  selectConfiguratorInstallation,
  selectConfiguratorAddon,
  resetConfiguratorAddons,
  resetInstallationConfigurator
} from 'views/ProductPage/configurators/installation/actions';
import styles from 'views/ProductPage/configurators/installation/themes/BGH/styles.scss';
import { isInstallationSelected } from 'views/ProductPage/configurators/installation/selector';
import Markdown from 'components/Shared/Markdown';
import { onDeleteValue, onSelectValue } from 'views/ProductPage/configurators/actions';
import { getConfiguratorVariables } from 'views/ProductPage/selector';
import { isMobile } from 'components/Shared/responsive';
import { InstallationContainer } from 'views/ProductPage/configurators/installation/installationContainer';
import cn from 'classnames';
import { InstallationAddon } from 'views/ProductPage/configurators/installation/themes/BGH/installationAddon';
import { ToolTipDialog } from 'components/Shared/ToolTip/themes/BGH-RD3';
import { ZipCodeInput } from 'components/Shared/ZipCodeInput';
import { useDispatch } from 'react-redux';
import { getFormattedZipCode } from 'components/Shared/ZipCodeInput/themes/BGH/validation';
import { GTMInstallationZipCode } from 'store/googleTagManager/actions';

interface OwnProps extends ConfiguratorProps {
  configurator: PolarpumpInstallationConfigurator;
  showInstallation: boolean;
}

interface InstallationProps {
  selectInstallation: typeof selectConfiguratorInstallation;
  selectAddon: typeof selectConfiguratorAddon;
  resetAddons: typeof resetConfiguratorAddons;
  resetConfigurator: typeof resetInstallationConfigurator;
  isInstallationSelected: boolean;
  zipCode: string;
  setZipCode: ChangeEventHandler<HTMLInputElement>;
}

interface MergedProps extends OwnProps, InstallationProps {}

interface OverridableProps extends OwnProps, Partial<InstallationProps> {}

interface OwnState {
  showInstallationOptions: boolean;
  showAddonOptions: boolean;
}

const initialState: OwnState = {
  showInstallationOptions: false,
  showAddonOptions: false
};

type OwnAction =
  | { type: 'toggleInstallation'; show: boolean }
  | { type: 'toggleAddons'; show: boolean };

const ownReducer: Reducer<OwnState, OwnAction> = (state, action) => {
  switch (action.type) {
    case 'toggleInstallation':
      return { ...state, showInstallationOptions: action.show };
    case 'toggleAddons':
      return { ...state, showAddonOptions: action.show };
    default:
      return state;
  }
};

const getPolarPumpProducts = (group?: InstallationAddonGroup): PolarPumpAddonProduct[] => {
  if (!group) {
    return [];
  }

  if (group.products.every(p => p.type === InstallationProducType.PolarPump)) {
    return group.products as PolarPumpAddonProduct[];
  }
  return [];
};

const PolarPumpConfigurator = ({
  configurator,
  showInstallation,
  isInstallationSelected,
  selectInstallation,
  selectAddon,
  resetAddons,
  resetConfigurator,
  zipCode,
  setZipCode,
  isMobile,
  inDrawer
}: MergedProps) => {
  const [state, dispatch] = useReducer(ownReducer, initialState);
  const reduxDispatch = useDispatch();

  const formattedZipCode = getFormattedZipCode(zipCode);

  const installationProducts = getPolarPumpProducts(configurator.installation);
  const addonProducts = getPolarPumpProducts(configurator.addons);

  const installationDescription = configurator.installation
    ? configurator.installation.description
    : undefined;
  const addonsDescription = configurator.addons ? configurator.addons.description : undefined;

  const filteredInstallations = useMemo(
    () =>
      installationProducts.filter(p =>
        p.zipCodeRanges ? isZipCodeInRange(formattedZipCode, p.zipCodeRanges) : true
      ),
    [formattedZipCode, installationProducts]
  );

  const filteredAddons = useMemo(
    () =>
      addonProducts.filter(p =>
        p.zipCodeRanges ? isZipCodeInRange(formattedZipCode, p.zipCodeRanges) : true
      ),
    [formattedZipCode, addonProducts]
  );

  const installationTooltip = useMemo(
    () => (installationDescription ? <Markdown {...installationDescription} /> : NO_ELEMENT),
    [installationDescription]
  );

  const noAddonsSelected = useMemo(() => addonProducts.every(p => !p.selected), [addonProducts]);
  const addonTooltip = useMemo(() => <Markdown {...addonsDescription} />, [addonsDescription]);
  const noInstallationsFound =
    isValidZipcode(formattedZipCode) && filteredInstallations.length === 0;

  useEffect(() => {
    if (isValidZipcode(formattedZipCode)) {
      //If we have filteredInstallations, the Zipcode is in the accepted range
      reduxDispatch(GTMInstallationZipCode(filteredInstallations.length > 0));

      dispatch({ type: 'toggleInstallation', show: true });
      if (isInstallationSelected) {
        dispatch({ type: 'toggleAddons', show: true });
      }

      return;
    }

    dispatch({ type: 'toggleInstallation', show: false });
    dispatch({ type: 'toggleAddons', show: false });

    resetConfigurator();
  }, [
    zipCode,
    isInstallationSelected,
    resetConfigurator,
    filteredInstallations.length,
    reduxDispatch,
    formattedZipCode
  ]);

  const groupName = 'addons';

  const installationOptions = state.showInstallationOptions ? (
    <div>
      <span className={styles.sectionHeading}>
        {rm('product_polarpump_installation_type')}
        <ToolTipDialog text={installationTooltip} />
      </span>
      <fieldset id="installation">
        {filteredInstallations.map(product => (
          <InstallationAddon
            key={product.productId}
            isSelected={product.selected}
            onClick={selectInstallation}
            title={product.title}
            price={product.price}
            value={product.productId}
            name={groupName}
          />
        ))}
      </fieldset>
    </div>
  ) : (
    NO_ELEMENT
  );

  const addonOptions =
    state.showAddonOptions && configurator.addons ? (
      <div>
        <span className={styles.sectionHeading}>
          {rm('product_polarpump_installation_addons')}
          <ToolTipDialog text={addonTooltip} />
        </span>
        <fieldset id="addons">
          <InstallationAddon
            isSelected={noAddonsSelected}
            onClick={resetAddons}
            title={rm('product_polarpump_installation_addons_product_default_name')}
            value=""
            name={groupName}
          />
          {filteredAddons.map(product => (
            <InstallationAddon
              key={product.productId}
              isSelected={product.selected}
              onClick={selectAddon}
              title={product.title}
              price={product.price}
              value={product.productId}
              name={groupName}
            />
          ))}
        </fieldset>
      </div>
    ) : (
      NO_ELEMENT
    );

  const title = showInstallation
    ? rm('product_polarpump_header')
    : rm('product_polarpump_installation_disabled');

  const mobileOrDrawer = isMobile || inDrawer;

  return (
    <InstallationContainer
      title={title}
      disabled={!showInstallation}
      initialOpen={showInstallation}
      isMobile={isMobile}
      inDrawer={inDrawer}
      configurator={configurator}
    >
      <div
        className={cn(styles.body, {
          [styles.mobile]: mobileOrDrawer
        })}
      >
        <ZipCodeInput
          title={rm('product_polarpump_installation_zipcode_header')}
          description={rm('product_polarpump_body')}
          inputClassName={styles.zipInput}
          descriptionClassName={styles.description}
          onChange={setZipCode}
          zipCode={zipCode}
        />
        {noInstallationsFound ? (
          <p className={styles.description}>
            {rm('product_polarpump_installation_error_not_in_area')}
          </p>
        ) : (
          <>
            {installationOptions}
            {addonOptions}
          </>
        )}
      </div>
    </InstallationContainer>
  );
};

interface StateProps {
  inputs: Record<string, string>;
}

export default connect<MergedProps, OverridableProps, StateProps>(
  state => ({
    isMobile: isMobile(state),
    isInstallationSelected: isInstallationSelected(state.currentPage),
    inputs: getConfiguratorVariables(state.currentPage)
  }),
  dispatch => ({
    selectInstallation: (productId: string) => dispatch(selectConfiguratorInstallation(productId)),
    selectAddon: (productId: string) => dispatch(selectConfiguratorAddon(productId)),
    resetAddons: () => dispatch(resetConfiguratorAddons()),
    resetConfigurator: () => dispatch(resetInstallationConfigurator()),
    setZipCode: ({ target: { value } }) =>
      dispatch(value ? onSelectValue('zipCode', value) : onDeleteValue('zipCode'))
  }),
  ({ inputs, ...state }, dispatch, props) => ({
    ...state,
    ...dispatch,
    ...props,
    zipCode: props.zipCode || inputs.zipCode || ''
  })
)(PolarPumpConfigurator);
